/**
 * Switching workspace, renaming one, deleting one and making another, in one
 * small window.
 *
 * A WORKSPACE IS A BRAND. The data model calls it a brand everywhere and always
 * will, because `brand_id` is the scope every repository call carries. The
 * chrome has called it a workspace to a person's face since the sidebar was
 * built, and the control that opens this window is already labelled "Switch
 * workspace". So this window says workspace, and the one place it says brand is
 * "brand file", which is the name of a document rather than a second name for
 * the container. One thing, one word, in the words the operator already reads.
 *
 * WHAT A NON-ADMIN SEES, WHICH IS THE DECISION THIS FILE HAD TO TAKE. Today
 * renaming, creating and deleting live behind the platform admin flag on the
 * brands settings screen, so a member does not know they exist. The reference
 * puts all four verbs in one window for everybody. This shows all four to
 * everybody and DISABLES the three that are not theirs, with the reason printed
 * under them rather than only in a tooltip.
 *
 * Hiding them would have been the smaller change. It is the wrong one here for
 * the reason the design language already gives about the shortcuts drawer:
 * state the precondition, do not hide the row. A member who has been told by a
 * colleague that they can rename a workspace, and who opens this and finds a
 * bare dropdown, learns nothing and asks somebody. A member who finds a greyed
 * pencil and a sentence saying an admin has to do it knows what to do next, and
 * it costs one line of copy.
 *
 * THE DISABLED STATE IS A COURTESY AND NOT THE GATE. Every one of these writes
 * is refused by the server on its own: creating needs the platform admin role,
 * and renaming and deleting need admin or owner on that particular workspace.
 * Nothing here is load bearing for that.
 *
 * WHICH IS ALSO WHY `canManage` IS A PROP. The session publishes the platform
 * role and does not publish the per-workspace membership role, so the browser
 * cannot work out the server's real rule for renaming and deleting. The
 * platform flag is the closest thing it has, and it is deliberately the
 * narrower of the two: it greys a control out for somebody the server would
 * have allowed, rather than offering a verb that is going to fail. When the
 * session grows a per-workspace role, pass that here and the copy stops being
 * an approximation.
 *
 * The hooks are the ones the rest of the app already uses, so a workspace made
 * here and a workspace made on the settings screen go through exactly the same
 * call and the same cache invalidation. `SidebarWorkspace` keeps its own small
 * menu: this window is opened from it, not by it.
 */
import { useEffect, useState } from 'react'
import { cn } from '../lib/cn'
import { useBrandSwitch } from '../lib/session'
import type { SessionBrand } from '../lib/types'
import { Button } from './Button'
import { ConfirmDialog } from './ConfirmDialog'
import { Field } from './Field'
import { BrandMark } from './Identity'
import { Input, Select } from './Input'
import { Modal } from './Modal'
import { ErrorState } from './States'
import { useToast } from './Toast'
import { Tooltip } from './Tooltip'
import { IconBrands, IconTrash } from './icons'
import { useCreateBrand, useUpdateBrand } from '../pages/admin/api'
import { useDeleteBrand } from '../pages/dashboard/ui/data'

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Every sentence this window says in its own voice, in one place so the house
 * rules can be held to it, the way `SIDEBAR_COPY` already is. The sentences
 * that carry a name are the builders below, for the same reason.
 */
export const WORKSPACE_COPY = {
  title: 'Workspaces',
  picker: 'Workspace',
  /** For the one sentence that names a workspace after it has stopped being on the list. */
  fallbackName: 'that workspace',
  pickerHint: 'Picking one re-scopes every list, chart and inbox behind this window.',
  nameLabel: 'Workspace name',
  namePlaceholder: 'Enter new workspace name',
  create: 'Create a new workspace',
  createHint:
    'The short name used in links is made from this one, and that short name cannot be changed later.',
  rename: 'Save the new name',
  renameStart: 'Rename this workspace',
  cancelRename: 'Cancel',
  deleteStart: 'Delete this workspace',
  deleteConfirm: 'Delete it',
  cascade:
    'Its brand file, its offers, its channel pinning and its memberships go with it. If it still holds people, a suppression, a campaign or anything ever sent, this is refused and nothing changes.',
  deletingActive: 'This is the workspace you are working in right now.',
  managedByAdmin:
    'Only an admin can rename, delete or create a workspace. Ask one to do it for you.',
  tooShort: 'A workspace needs a name of at least two characters.',
  unchanged: 'That is already the name of this workspace.',
  gone: 'That workspace is not on the list any more.',
  noWorkspaces: 'You are not a member of any workspace yet. An admin can add you to one.',
  fileInvalid: 'This workspace cannot launch anything until its brand file is valid.',
  switchFailed: 'Could not switch workspace',
  scoped: 'Everything on screen is scoped to this workspace.',
  created: 'Pick it from the list to start working in it.',
  renamed: 'The short name used in links is unchanged.',
  deleted: 'It is gone from the list of workspaces.',
} as const

/** Which workspace the one text field is about, said where the field is read. */
export function renameHint(name: string): string {
  return `This renames ${name}. The short name used in links does not change, because it is in links people have already saved.`
}

/**
 * The server's own refusal, said before the request rather than after it. It
 * refuses a rename onto a name another workspace holds, and a create collides
 * the same way because the short name is made from the name. Subtler
 * collisions, two names that reduce to the same short name, are still the
 * server's to catch and its sentence is the one that gets shown.
 */
export function nameTakenSentence(name: string): string {
  return `Another workspace is already called ${name}.`
}

export function deleteTitle(name: string): string {
  return `Delete ${name}?`
}

// ---------------------------------------------------------------------------
// The decisions, as plain functions
// ---------------------------------------------------------------------------

/**
 * What the one text field is doing. The reference draws a single field and a
 * single button, and renaming and creating both want exactly that field, so the
 * field has a mode rather than the window having two of them.
 */
export type WorkspaceFormMode = { kind: 'create' } | { kind: 'rename'; brandId: string }

export interface WorkspaceFormPlan {
  submitLabel: string
  placeholder: string
  hint: string
  /** Why the button cannot act. Null means it can. */
  blocked: string | null
  /**
   * The same sentence, minus the one case where printing it would be nagging:
   * an empty field nobody has typed into yet. Null means print nothing.
   */
  reason: string | null
}

/**
 * The workspace the pencil and the trash act on, which is also the one the
 * select shows.
 *
 * `picked` is what this session last asked for and `current` is what the server
 * session says. They are the same a moment after a switch lands, and they
 * differ while one is in flight, after one fails, and after the picked one is
 * deleted. Resolving against the list rather than trusting either is what stops
 * the window pointing at a workspace that is no longer there.
 */
export function resolveWorkspaceSelection<T extends { id: string }>(
  brands: readonly T[],
  picked: string | null,
  current: string | null,
): T | null {
  const first: T | undefined = brands[0]
  return brands.find((brand) => brand.id === picked) ?? brands.find((brand) => brand.id === current) ?? first ?? null
}

export function workspaceFormPlan({
  mode,
  name,
  brands,
  canManage,
}: {
  mode: WorkspaceFormMode
  name: string
  brands: readonly { id: string; name: string }[]
  canManage: boolean
}): WorkspaceFormPlan {
  const renaming = mode.kind === 'rename'
  const target = renaming ? (brands.find((brand) => brand.id === mode.brandId) ?? null) : null
  const trimmed = name.trim()

  const shape = {
    submitLabel: renaming ? WORKSPACE_COPY.rename : WORKSPACE_COPY.create,
    placeholder: WORKSPACE_COPY.namePlaceholder,
    hint: target === null ? WORKSPACE_COPY.createHint : renameHint(target.name),
  }

  const blocked = ((): string | null => {
    if (!canManage) return WORKSPACE_COPY.managedByAdmin
    if (renaming && target === null) return WORKSPACE_COPY.gone
    if (trimmed.length < 2) return WORKSPACE_COPY.tooShort
    if (target !== null && trimmed === target.name) return WORKSPACE_COPY.unchanged
    const clash = brands.find(
      (brand) => brand.id !== target?.id && brand.name.toLowerCase() === trimmed.toLowerCase(),
    )
    return clash === undefined ? null : nameTakenSentence(clash.name)
  })()

  // An empty field is the resting state of a create, so saying the name is too
  // short before anybody has typed is nagging. Everything else is a thing the
  // person did and wants an answer to, and the admin sentence is the reason the
  // field is greyed at all, so it is said straight away.
  const quiet = blocked === WORKSPACE_COPY.tooShort && trimmed === ''

  return { ...shape, blocked, reason: blocked === null || quiet ? null : blocked }
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

/**
 * A pencil, drawn here rather than in the icon set.
 *
 * `icons.tsx` is shared by every screen and is edited from several directions
 * at once, so the one glyph this window needs is local rather than racing
 * another change into a file nobody owns. `SidebarWorkspace` keeps its collapse
 * glyph for the same reason. Same 24 grid and same stroke weight as the set, so
 * it sits beside the trash without looking borrowed.
 */
function IconPencil({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
    >
      <path d="M4.5 19.5h3.2l9.6-9.6a2.26 2.26 0 0 0-3.2-3.2l-9.6 9.6z" />
      <path d="M13.8 7.4l2.8 2.8" />
    </svg>
  )
}

export interface WorkspacePanelProps {
  brands: readonly SessionBrand[]
  /** The row the pencil and the trash act on. Null only when there are none. */
  selected: SessionBrand | null
  canManage: boolean
  mode: WorkspaceFormMode
  name: string
  plan: WorkspaceFormPlan
  switching: boolean
  saving: boolean
  /** A failed create or rename. The delete's failure belongs to its own dialog. */
  error?: unknown
  onPick: (brandId: string) => void
  onRenameStart: () => void
  onRenameCancel: () => void
  onDeleteStart: () => void
  onNameChange: (name: string) => void
  onSubmit: () => void
}

/**
 * Everything inside the window, and nothing that portals.
 *
 * Split out for the same reason `MenuPanel` and `TooltipBubble` are: there is
 * no DOM test environment here, so the only way to read this markup in a test
 * is `react-dom/server`, and a portal cannot be server rendered at all. The
 * modal chrome and the confirmation both portal. This does not.
 */
export function WorkspacePanel({
  brands,
  selected,
  canManage,
  mode,
  name,
  plan,
  switching,
  saving,
  error,
  onPick,
  onRenameStart,
  onRenameCancel,
  onDeleteStart,
  onNameChange,
  onSubmit,
}: WorkspacePanelProps) {
  const iconRefusal = canManage ? null : WORKSPACE_COPY.managedByAdmin

  return (
    <div className="space-y-4">
      {brands.length === 0 ? (
        <p className="text-sm leading-relaxed text-ink-muted">{WORKSPACE_COPY.noWorkspaces}</p>
      ) : (
        <Field
          label={WORKSPACE_COPY.picker}
          /* The launch block belongs to the picker rather than to a line of its
             own under it, because `Field` puts its hint in the select's
             `aria-describedby`. Read out with the control it is about, a screen
             reader hears why this workspace cannot send anything at the moment
             it lands on the way to change workspace. */
          hint={
            <>
              {WORKSPACE_COPY.pickerHint}
              {selected?.knowledgeValid === false ? (
                <span className="mt-1 block text-warning-text">{WORKSPACE_COPY.fileInvalid}</span>
              ) : null}
            </>
          }
        >
          <div className="flex items-center gap-2">
            <BrandMark
              brandId={selected?.id}
              name={selected?.name ?? WORKSPACE_COPY.picker}
              logoSvg={selected?.logoSvg}
              size="md"
            />
            <div className="min-w-0 flex-1">
              <Select
                value={selected?.id ?? ''}
                disabled={switching}
                onChange={(event) => onPick(event.target.value)}
                options={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
              />
            </div>
            <Tooltip content={iconRefusal ?? WORKSPACE_COPY.renameStart}>
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                aria-label={WORKSPACE_COPY.renameStart}
                disabled={!canManage || selected === null}
                onClick={onRenameStart}
              >
                <IconPencil className="h-4 w-4" />
              </Button>
            </Tooltip>
            {/* The reference's pair of outlined icon buttons, secondary and small,
                rather than a solid red block. A delete sitting beside a rename
                should not be the loudest thing in the window, and the red waits
                for the confirmation, where the meaning is. */}
            <Tooltip content={iconRefusal ?? WORKSPACE_COPY.deleteStart}>
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                aria-label={WORKSPACE_COPY.deleteStart}
                disabled={!canManage || selected === null}
                onClick={onDeleteStart}
              >
                <IconTrash className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        </Field>
      )}

      <Field
        label={WORKSPACE_COPY.nameLabel}
        hint={plan.hint}
        aside={
          mode.kind === 'rename' ? (
            <Button variant="link" size="sm" onClick={onRenameCancel}>
              {WORKSPACE_COPY.cancelRename}
            </Button>
          ) : null
        }
      >
        <Input
          value={name}
          placeholder={plan.placeholder}
          disabled={!canManage}
          onChange={(event) => onNameChange(event.target.value)}
          onKeyDown={(event) => {
            // Enter is how anybody types a name into a single field and expects
            // it to be taken. `isComposing` is not belt and braces here: this
            // console writes outreach in Japanese and Korean, and an IME takes
            // Enter to commit the characters being composed, so without the
            // guard the first Enter of a Japanese name would submit a
            // half-finished one.
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
            event.preventDefault()
            onSubmit()
          }}
        />
      </Field>

      {error ? <ErrorState error={error} /> : null}

      {plan.reason === null ? null : (
        <p className="text-xs leading-relaxed text-ink-muted">{plan.reason}</p>
      )}

      <Button
        variant="primary"
        fullWidth
        loading={saving}
        disabled={plan.blocked !== null}
        onClick={onSubmit}
      >
        {plan.submitLabel}
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The window
// ---------------------------------------------------------------------------

export interface WorkspaceModalProps {
  open: boolean
  onClose: () => void
  /** Every workspace this person belongs to, in the order the sidebar lists them. */
  brands: readonly SessionBrand[]
  /** The one every screen behind this window is scoped to. */
  currentBrandId: string | null
  /** Whether renaming, deleting and creating are offered or greyed. See the top of this file. */
  canManage: boolean
}

export function WorkspaceModal({ open, onClose, brands, currentBrandId, canManage }: WorkspaceModalProps) {
  const switchBrand = useBrandSwitch()
  const create = useCreateBrand()
  const update = useUpdateBrand()
  const remove = useDeleteBrand()
  const toast = useToast()

  const [picked, setPicked] = useState<string | null>(null)
  const [mode, setMode] = useState<WorkspaceFormMode>({ kind: 'create' })
  const [name, setName] = useState('')
  const [deleting, setDeleting] = useState<SessionBrand | null>(null)

  // Opening is the only moment this is a blank slate. Reopening onto a name
  // somebody typed a week ago, or onto a refusal from a request that has since
  // been forgotten, is the defect this closes.
  useEffect(() => {
    if (!open) return
    setPicked(null)
    setMode({ kind: 'create' })
    setName('')
    setDeleting(null)
    create.reset()
    update.reset()
    remove.reset()
  }, [open])

  const selected = resolveWorkspaceSelection(brands, picked, currentBrandId)
  const plan = workspaceFormPlan({ mode, name, brands, canManage })
  const saving = create.isPending || update.isPending
  const nameOf = (brandId: string): string =>
    brands.find((brand) => brand.id === brandId)?.name ?? WORKSPACE_COPY.fallbackName

  /**
   * Picking switches, and the window stays open.
   *
   * The select is both the switcher and the thing the pencil and the trash act
   * on, so a surface that closed itself on a pick would be unusable for three
   * of its four verbs. Everything behind it has already re-scoped and the brand
   * edge has already changed colour, and the toast says so.
   */
  const pick = (brandId: string): void => {
    if (brandId === '' || brandId === selected?.id) return
    setPicked(brandId)
    // A rename in progress belongs to the workspace that was selected when it
    // started. Carrying the half-typed name across to another one is how a
    // workspace quietly gets the wrong name.
    if (mode.kind === 'rename') {
      setMode({ kind: 'create' })
      setName('')
    }
    switchBrand.mutate(brandId, {
      onSuccess: () => toast.success(`Now working in ${nameOf(brandId)}`, WORKSPACE_COPY.scoped),
      onError: (error) => {
        setPicked(null)
        toast.error(WORKSPACE_COPY.switchFailed, error)
      },
    })
  }

  const submit = (): void => {
    if (plan.blocked !== null || saving) return
    const trimmed = name.trim()
    if (mode.kind === 'rename') {
      update.mutate(
        { brandId: mode.brandId, name: trimmed },
        {
          onSuccess: (result) => {
            toast.success(`Now called ${result.name}`, WORKSPACE_COPY.renamed)
            setMode({ kind: 'create' })
            setName('')
          },
        },
      )
      return
    }
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (result) => {
          toast.success(`${result.name} created`, WORKSPACE_COPY.created)
          setName('')
        },
      },
    )
  }

  const confirmDelete = (): void => {
    const target = deleting
    if (target === null) return
    remove.mutate(target.id, {
      onSuccess: () => {
        toast.success(`${target.name} deleted`, WORKSPACE_COPY.deleted)
        setDeleting(null)
        // Follow the session again rather than keeping a pick that names a row
        // the server has just dropped.
        setPicked(null)
        if (mode.kind === 'rename' && mode.brandId === target.id) {
          setMode({ kind: 'create' })
          setName('')
        }
      },
    })
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={WORKSPACE_COPY.title}
        icon={<IconBrands className="h-5 w-5" />}
        size="base"
        /*
         * Shut while the confirmation is up, and while a write is in flight.
         *
         * `useOnEscape` attaches both listeners to the document, so one Escape
         * reaches this window and the confirmation over it together, and
         * `stopPropagation` does not separate two listeners on the same node.
         * Refusing to be dismissed here is what makes Escape answer the
         * question in front of the person rather than closing the room it was
         * asked in. Switching is left dismissible: closing mid-switch loses
         * nothing, because the session is what is changing.
         */
        dismissible={deleting === null && !saving}
      >
        <WorkspacePanel
          brands={brands}
          selected={selected}
          canManage={canManage}
          mode={mode}
          name={name}
          plan={plan}
          switching={switchBrand.isPending}
          saving={saving}
          error={create.error ?? update.error}
          onPick={pick}
          onRenameStart={() => {
            if (selected === null) return
            setMode({ kind: 'rename', brandId: selected.id })
            setName(selected.name)
          }}
          onRenameCancel={() => {
            setMode({ kind: 'create' })
            setName('')
          }}
          onDeleteStart={() => setDeleting(selected)}
          onNameChange={setName}
          onSubmit={submit}
        />
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title={deleteTitle(deleting?.name ?? WORKSPACE_COPY.fallbackName)}
        description={
          deleting !== null && deleting.id === currentBrandId
            ? `${WORKSPACE_COPY.deletingActive} ${WORKSPACE_COPY.cascade}`
            : WORKSPACE_COPY.cascade
        }
        confirmLabel={WORKSPACE_COPY.deleteConfirm}
        tone="danger"
        loading={remove.isPending}
        error={remove.error}
        onClose={() => {
          setDeleting(null)
          remove.reset()
        }}
        onConfirm={confirmDelete}
      />
    </>
  )
}
